/**
 * Billing Store
 *
 * State management for Platform Billing Dashboard.
 * Handles billing aggregation data for enterprises, partners, and organizations.
 */

import { create } from 'zustand';
import { apiService } from '../services/api';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PlatformBillingOverview {
  totalMRR: number;
  totalActiveSubscriptions: number;
  totalOrganizations: number;
  pendingChangeRequests: number;
  overageRevenue: number;
  tierBreakdown: {
    tier: string;
    count: number;
    mrr: number;
  }[];
  statusBreakdown: {
    status: string;
    count: number;
  }[];
}

export interface EnterpriseBillingData {
  enterpriseId: string;
  name: string;
  slug: string;
  status: string;
  type: string;
  partnersCount: number;
  partnersQuota: number;
  organizationsCount: number;
  organizationsQuota: number;
  totalMRR: number;
  activeSubscriptions: number;
  discountPercent?: number;
}

export interface PartnerBillingData {
  partnerId: string;
  name: string;
  slug: string;
  status: string;
  type: string;
  enterpriseId?: string;
  enterpriseName?: string;
  organizationsCount: number;
  organizationsQuota: number;
  totalMRR: number;
  activeSubscriptions: number;
  discountPercent?: number;
  quotaStatus: {
    current: number;
    max: number;
    percent: number;
  };
}

export interface OrganizationBillingData {
  organizationId: string;
  name: string;
  subdomain?: string;
  partnerId?: string;
  partnerName?: string;
  subscription?: {
    planId?: string;
    planName?: string;
    status: string;
    tier?: string;
    monthlyAmount: number;
    usage: {
      encounters: { used: number; limit: number; percent: number };
      minutes: { used: number; limit: number; percent: number };
      users: { current: number; limit: number };
    };
    overageAmount: number;
    periodEnd?: string;
  };
}

export interface ChangeRequest {
  id: string;
  organizationId: string;
  organizationName: string;
  currentPlanId?: string;
  currentPlanName?: string;
  requestedPlanId: string;
  requestedPlanName: string;
  requestType: 'upgrade' | 'downgrade' | 'change';
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedBy: {
    userId: string;
    email: string;
    name?: string;
  };
  createdAt: string;
  billingImpact?: {
    currentMonthly: number;
    newMonthly: number;
    proratedCredit?: number;
    proratedCharge?: number;
  };
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface BillingState {
  // Platform overview
  platformOverview: PlatformBillingOverview | null;
  platformOverviewLoading: boolean;
  platformOverviewError: string | null;

  // Enterprise billing
  enterprises: EnterpriseBillingData[];
  enterprisesLoading: boolean;
  enterprisesError: string | null;

  // Partner billing
  partners: PartnerBillingData[];
  partnersLoading: boolean;
  partnersError: string | null;

  // Organization billing
  organizations: OrganizationBillingData[];
  organizationsLoading: boolean;
  organizationsError: string | null;

  // Change requests
  changeRequests: ChangeRequest[];
  changeRequestsLoading: boolean;
  changeRequestsError: string | null;
  pendingCount: number;

  // Actions
  fetchPlatformOverview: () => Promise<void>;
  fetchEnterprises: () => Promise<void>;
  fetchPartners: (enterpriseId?: string) => Promise<void>;
  fetchOrganizations: (partnerId?: string) => Promise<void>;
  fetchChangeRequests: (status?: string) => Promise<void>;
  approveChangeRequest: (id: string, password: string) => Promise<void>;
  rejectChangeRequest: (id: string, reason: string) => Promise<void>;
  reset: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useBillingStore = create<BillingState>((set, get) => ({
  // Initial state
  platformOverview: null,
  platformOverviewLoading: false,
  platformOverviewError: null,

  enterprises: [],
  enterprisesLoading: false,
  enterprisesError: null,

  partners: [],
  partnersLoading: false,
  partnersError: null,

  organizations: [],
  organizationsLoading: false,
  organizationsError: null,

  changeRequests: [],
  changeRequestsLoading: false,
  changeRequestsError: null,
  pendingCount: 0,

  // Actions
  fetchPlatformOverview: async () => {
    set({ platformOverviewLoading: true, platformOverviewError: null });
    try {
      const response = await apiService.get<{ success: boolean; data: PlatformBillingOverview }>(
        '/v1/billing/platform/overview'
      );
      set({
        platformOverview: response.data,
        platformOverviewLoading: false,
        pendingCount: response.data.pendingChangeRequests
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      set({
        platformOverviewLoading: false,
        platformOverviewError: err.response?.data?.error || err.message || 'Failed to fetch platform overview'
      });
    }
  },

  fetchEnterprises: async () => {
    set({ enterprisesLoading: true, enterprisesError: null });
    try {
      const response = await apiService.get<{ success: boolean; data: EnterpriseBillingData[] }>(
        '/v1/billing/enterprises'
      );
      set({
        enterprises: response.data,
        enterprisesLoading: false
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      set({
        enterprisesLoading: false,
        enterprisesError: err.response?.data?.error || err.message || 'Failed to fetch enterprises'
      });
    }
  },

  fetchPartners: async (enterpriseId?: string) => {
    set({ partnersLoading: true, partnersError: null });
    try {
      const url = enterpriseId
        ? `/v1/billing/partners?enterpriseId=${enterpriseId}`
        : '/v1/billing/partners';
      const response = await apiService.get<{ success: boolean; data: PartnerBillingData[] }>(url);
      set({
        partners: response.data,
        partnersLoading: false
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      set({
        partnersLoading: false,
        partnersError: err.response?.data?.error || err.message || 'Failed to fetch partners'
      });
    }
  },

  fetchOrganizations: async (partnerId?: string) => {
    set({ organizationsLoading: true, organizationsError: null });
    try {
      const url = partnerId
        ? `/v1/billing/organizations?partnerId=${partnerId}`
        : '/v1/billing/organizations';
      const response = await apiService.get<{ success: boolean; data: OrganizationBillingData[] }>(url);
      set({
        organizations: response.data,
        organizationsLoading: false
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      set({
        organizationsLoading: false,
        organizationsError: err.response?.data?.error || err.message || 'Failed to fetch organizations'
      });
    }
  },

  fetchChangeRequests: async (status?: string) => {
    set({ changeRequestsLoading: true, changeRequestsError: null });
    try {
      const url = status
        ? `/v1/platform/subscriptions/change-requests?status=${status}`
        : '/v1/platform/subscriptions/change-requests';
      // Backend returns { data: { requests: [...], pendingCount } }
      const response = await apiService.get<{
        success: boolean;
        data: { requests: Record<string, unknown>[]; pendingCount: number };
      }>(url);

      // Map backend fields to frontend interface
      const rawRequests = response.data.requests || [];
      const changeRequests: ChangeRequest[] = rawRequests.map((r: Record<string, unknown>) => ({
        id: r.id as string,
        organizationId: r.organizationId as string,
        organizationName: (r.organizationName as string) || 'Unknown',
        currentPlanId: r.currentPlanId as string | undefined,
        currentPlanName: r.currentPlanName as string | undefined,
        requestedPlanId: r.requestedPlanId as string,
        requestedPlanName: (r.requestedPlanName as string) || '',
        // Backend uses changeType, frontend uses requestType
        requestType: ((r.changeType || r.requestType || 'change') as ChangeRequest['requestType']),
        status: r.status as ChangeRequest['status'],
        // Backend enriches requestedBy as { id, firstName, lastName, email }
        requestedBy: r.requestedBy && typeof r.requestedBy === 'object'
          ? {
              userId: (r.requestedBy as Record<string, string>).id,
              email: (r.requestedBy as Record<string, string>).email,
              name: [
                (r.requestedBy as Record<string, string>).firstName,
                (r.requestedBy as Record<string, string>).lastName
              ].filter(Boolean).join(' ') || undefined
            }
          : { userId: (r.requestedBy as string) || '', email: '' },
        createdAt: (r.requestedAt || r.createdAt) as string,
        billingImpact: r.billingImpact as ChangeRequest['billingImpact'],
      }));

      set({
        changeRequests,
        changeRequestsLoading: false,
        pendingCount: response.data.pendingCount ?? changeRequests.filter(r => r.status === 'pending').length
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      set({
        changeRequestsLoading: false,
        changeRequestsError: err.response?.data?.error || err.message || 'Failed to fetch change requests'
      });
    }
  },

  approveChangeRequest: async (id: string, password: string) => {
    try {
      await apiService.post(`/v1/platform/subscriptions/change-requests/${id}/approve`, { password });
      // Refresh change requests after approval
      await get().fetchChangeRequests();
      await get().fetchPlatformOverview();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(err.response?.data?.error || err.message || 'Failed to approve change request');
    }
  },

  rejectChangeRequest: async (id: string, reason: string) => {
    try {
      await apiService.post(`/v1/platform/subscriptions/change-requests/${id}/reject`, { reason });
      // Refresh change requests after rejection
      await get().fetchChangeRequests();
      await get().fetchPlatformOverview();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(err.response?.data?.error || err.message || 'Failed to reject change request');
    }
  },

  reset: () => {
    set({
      platformOverview: null,
      platformOverviewLoading: false,
      platformOverviewError: null,
      enterprises: [],
      enterprisesLoading: false,
      enterprisesError: null,
      partners: [],
      partnersLoading: false,
      partnersError: null,
      organizations: [],
      organizationsLoading: false,
      organizationsError: null,
      changeRequests: [],
      changeRequestsLoading: false,
      changeRequestsError: null,
      pendingCount: 0
    });
  }
}));
